import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Navigate } from 'react-router';

import { useTypedParams } from '@sorare/routing';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { ANY_SPORT_MARKET_SHOP_MANAGER_SALES } from '@sorare/core/src/constants/__generated__/routes';
import {
  sportAnalyticTag,
  useSportContext,
} from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { createCardSorts } from '@sorare/core/src/lib/algolia';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { MarketStackTitle } from '@sorare/marketplace/src/components/market/MarketStackTitle';
import { MarketRoot } from '@sorare/marketplace/src/components/market/ui';
import { useDefaultFilters } from '@sorare/marketplace/src/hooks/useDefaultFilters';
import { AdvancedCardSearch } from '@sorare/marketplace/src/searchCards';
import { secondaryMarketStackedFilters as footballFilters } from '@sorare/marketplace/src/searchCards/football/cardFilters';
import { secondaryMarketStackedFilters as mlbFilters } from '@sorare/marketplace/src/searchCards/mlb/cardFilters';
import { secondaryMarketStackedFilters as nbaFilters } from '@sorare/marketplace/src/searchCards/nba/cardFilters';

import type {
  ManagerSalesStackPlayerQuery,
  ManagerSalesStackPlayerQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const SECONDARY_MARKET_STACK_PLAYER_QUERY = gql`
  query ManagerSalesStackPlayerQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      displayName
    }
  }
` as TypedDocumentNode<
  ManagerSalesStackPlayerQuery,
  ManagerSalesStackPlayerQueryVariables
>;

const secondaryMarketStackedFilters = {
  [Sport.FOOTBALL]: footballFilters,
  [Sport.BASEBALL]: mlbFilters,
  [Sport.NBA]: nbaFilters,
};

const ManagerSalesStackPage = () => {
  const { sport, generateSportContextPath } = useSportContext();
  const { playerSlug, rarity } = useTypedParams<PageParams>();
  const { data, loading } = useQuery(SECONDARY_MARKET_STACK_PLAYER_QUERY, {
    variables: {
      slug: playerSlug!,
    },
    skip: !playerSlug,
  });

  useTitleAndDescription(
    metadatas.transfers.title,
    metadatas.transfers.description
  );

  const cardSortsWithBestValue = createCardSorts({
    indexPrefix: 'Cards On Sale',
    withBestValue: sport === Sport.FOOTBALL,
    withHighestPrice: true,
    withEndingSoon: false,
  });

  const defaultSort = 'Cards On Sale Lowest Price';

  const filters = useDefaultFilters({
    secondary: true,
    rarity,
    playerSlug,
  });

  const cardFilters = secondaryMarketStackedFilters[sport];

  if (!cardFilters) {
    return (
      <Navigate
        to={generateSportContextPath(ANY_SPORT_MARKET_SHOP_MANAGER_SALES)}
      />
    );
  }

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!data?.anyPlayer || !rarity || !Object.keys(Rarity).includes(rarity)) {
    return (
      <Navigate
        to={generateSportContextPath(ANY_SPORT_MARKET_SHOP_MANAGER_SALES)}
      />
    );
  }

  return (
    <MarketRoot>
      <Portal id="page-header-title-breadcrumb">
        <MarketStackTitle
          player={data.anyPlayer}
          rarity={rarity as Rarity}
          sport={sport}
        />
      </Portal>
      <AdvancedCardSearch
        sport={sport}
        defaultSort={defaultSort}
        sorts={cardSortsWithBestValue}
        defaultFilters={filters}
        cardFilters={cardFilters}
        analyticsTags={['TransferMarketStack', sportAnalyticTag[sport]]}
        topic={{
          type: 'stack',
          label: data.anyPlayer.displayName,
        }}
        removeEndedSingleSaleOffers
        distinct={false}
      />
    </MarketRoot>
  );
};

export default ManagerSalesStackPage;
